import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import AddPetOwnerModal from "./AddPetOwnerModal";
import QuickAddPetModal from "./QuickAddPetModal";
import SelectPaymentModal from "./SelectPaymentModal"; // Commented out for now - will be used later
import { petAPI, clinicAPI, outpatientAPI, authUtils } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import { useClinicData } from "../../hooks/useClinicData";
import { usePaymentTypes } from "../../hooks/usePaymentTypes";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";
import { USER_TYPES } from "../../constants/userTypes";

const AdmitOutpatientModal = ({
  isOpen,
  onClose,
  onSave,
  vets = [],
  inpatientVisits = [],
  outpatientVisits = [],
  workingHours = [],
  appointmentTypes = [],
  prefillPetOwner = null,
  prefillPet = null,
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const fallbackLabels = useMemo(
    () => ({
      petGuardian: t("receptionOutpatients.admit.fallback.petGuardian"),
      pet: t("receptionOutpatients.admit.fallback.pet"),
      vet: t("receptionOutpatients.admit.fallback.vet"),
    }),
    [t]
  );
  const getVetDisplayName = useCallback(
    (vet) => {
      if (!vet) return "";
      return (
        vet.user?.name ||
        vet.name ||
        t("receptionOutpatients.admit.fields.vet.generic", {
          id: vet.user_id,
        })
      );
    },
    [t]
  );

  // Get clinic data using the hook
  const { getClinicId } = useClinicData();
  const [petOwnerSearch, setPetOwnerSearch] = useState("");
  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [petOwnerPets, setPetOwnerPets] = useState([]);
  const [isLoadingPets, setIsLoadingPets] = useState(false);
  const [petOwnerSearchResults, setPetOwnerSearchResults] = useState([]);
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [isSearchingPetOwners, setIsSearchingPetOwners] = useState(false);
  const [vetSearch, setVetSearch] = useState("");
  const [selectedVet, setSelectedVet] = useState(null);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [downpayment, setDownpayment] = useState(0);
  const [showAddPetOwnerModal, setShowAddPetOwnerModal] = useState(false);
  const [showAddPetModal, setShowAddPetModal] = useState(false);
  const { paymentTypes, loadingPaymentTypes } = usePaymentTypes();

  const availablePets = useMemo(() => {
    if (!petOwnerPets || petOwnerPets.length === 0) return [];

    return petOwnerPets.filter(pet => {
      // Exclude if pet is already in an active inpatient visit
      const hasActiveInpatient = (inpatientVisits || []).some(visit => {
        const visitPetId = visit.pet_id
          || visit.originalData?.pet_id
          || (visit.pet && typeof visit.pet === 'object' ? visit.pet.id : null)
          || (visit.originalData?.pet && typeof visit.originalData.pet === 'object' ? visit.originalData.pet.id : null)
          || visit.pet;

        if (!visitPetId) return false;
        if (parseInt(visitPetId) !== parseInt(pet.id)) return false;

        const status = String(visit.status || '').toLowerCase();
        return !['checked_out', 'completed', 'canceled', 'cancelled', 'closed', 'checkout'].includes(status);
      });

      if (hasActiveInpatient) return false;

      // Exclude if pet is already in an active outpatient visit
      const hasActiveOutpatient = (outpatientVisits || []).some(visit => {
        const visitPetId = visit.pet_id
          || visit.originalData?.pet_id
          || (visit.pet && typeof visit.pet === 'object' ? visit.pet.id : null)
          || (visit.originalData?.pet && typeof visit.originalData.pet === 'object' ? visit.originalData.pet.id : null)
          || visit.pet;

        if (!visitPetId) return false;
        if (parseInt(visitPetId) !== parseInt(pet.id)) return false;

        const status = String(visit.status || '').toLowerCase();
        return !['checked_out', 'completed', 'canceled', 'cancelled', 'closed', 'checkout'].includes(status);
      });

      return !hasActiveOutpatient;
    });
  }, [petOwnerPets, inpatientVisits, outpatientVisits]);

  // Commented out SelectPaymentModal state - will be used later
  // const [showSelectPaymentModal, setShowSelectPaymentModal] = useState(false);
  // const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  // const [paymentNotes, setPaymentNotes] = useState("");

  const [newOutpatient, setNewOutpatient] = useState({
    id: null,
    pet_owner: "",
    pet: "",
    vet: "",
    reason: "",
    date: "",
    time: "",
    type: "",
  });

  // Refs for Flatpickr
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);

  // Generate time slots based on working hours
  const generateTimeSlots = (workingHours) => {
    if (!workingHours || workingHours.length === 0) return [];

    const slots = [];
    workingHours.forEach(wh => {
      const dayIndex = dayStringToIndex(wh.day);
      if (dayIndex === undefined) {
        return; // skip invalid day
      }
      const startTime = wh.opening_time;
      const endTime = wh.closing_time;

      // Convert time strings to minutes for easier calculation
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);

      // Generate 30-minute slots
      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const timeString = minutesToTime(minutes);
        slots.push({
          time: timeString,
          display: timeString,
          day: wh.day,
          dayIndex: dayIndex
        });
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Normalize workingHours day values to Date.getDay() index (0=Sun..6=Sat)
  const dayStringToIndex = (inputDay) => {
    if (inputDay === undefined || inputDay === null) return undefined;
    const d = String(inputDay).toLowerCase();
    const fullMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    if (fullMap[d] !== undefined) return fullMap[d];
    const shortMap = {
      sun: 0,
      mon: 1,
      tue: 2,
      tues: 2,
      wed: 3,
      thu: 4,
      thur: 4,
      thurs: 4,
      fri: 5,
      sat: 6,
    };
    if (shortMap[d] !== undefined) return shortMap[d];
    const first3 = d.slice(0, 3);
    if (shortMap[first3] !== undefined) return shortMap[first3];
    const num = Number(d);
    if (!Number.isNaN(num) && num >= 0 && num <= 6) return num;
    return undefined;
  };

  const indexToShort = (idx) => ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][idx] || '';

  // When Pet Guardian changes, fetch their pets
  useEffect(() => {
    if (selectedPetOwner) {
      // Use pets from the search response if available, otherwise fall back to API call
      const petsFromSearch = selectedPetOwner.pets?.list || selectedPetOwner.pets || [];

      if (petsFromSearch.length > 0) {
        // Use pets from search response (no API call needed)
        setPetOwnerPets(petsFromSearch);

        // Auto-select if there's only one available pet
        if (availablePets.length === 1) {
          setSelectedPet(availablePets[0]);
        } else {
          setSelectedPet(null);
        }
        setIsLoadingPets(false);
      } else {
        // Fallback: fetch pets via API (for cases where search didn't include pets)
        setIsLoadingPets(true);
        petAPI.getOtherPets(selectedPetOwner.id)
          .then((res) => {
            const pets = res.data?.data || [];
            setPetOwnerPets(pets);

            // Auto-select if there's only one available pet
            if (availablePets.length === 1) {
              setSelectedPet(availablePets[0]);
            } else {
              setSelectedPet(null);
            }
          })
          .catch(() => {
            setPetOwnerPets([]);
            setSelectedPet(null);
          })
          .finally(() => {
            setIsLoadingPets(false);
          });
      }
    } else {
      setPetOwnerPets([]);
      setSelectedPet(null);
      setIsLoadingPets(false);
    }
  }, [selectedPetOwner]);

  // Allowed user types for the Pet Guardian / Client dropdown
  const allowedPetOwnerTypes = [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER];
  const filterAllowedPetOwners = (items) => (items || []).filter(item => {
    const userType = item.user_type || item.user?.user_type;
    return allowedPetOwnerTypes.includes(userType);
  });

  // Pet Guardian search with debouncing
  useEffect(() => {
    if (!petOwnerSearch.trim()) {
      setPetOwnerSearchResults([]);
      setIsSearchingPetOwners(false);
      return;
    }
    setIsSearchingPetOwners(true);
    const timeoutId = setTimeout(() => {
      clinicAPI.searchGuardians(petOwnerSearch)
        .then((response) => {
          const guardians = filterAllowedPetOwners(response.data?.data || []);

          // Also search within the local vets/staff list (Prop 'vets' contains clinic members)
          const searchTerm = petOwnerSearch.toLowerCase().trim();
          const matchingVets = filterAllowedPetOwners((vets || []).filter(vet => {
            const vetName = getVetDisplayName(vet).toLowerCase();
            return vetName.includes(searchTerm);
          }).map(vet => ({
            ...vet,
            display_name: getVetDisplayName(vet),
            is_staff: true,
            user_type: USER_TYPES.VETERINARIAN
          })));

          // Combine results and deduplicate by ID
          const combinedResults = [...guardians];
          matchingVets.forEach(mv => {
            // Check if this person is already in the guardian list
            if (!combinedResults.some(g => g.id === mv.id)) {
              combinedResults.push(mv);
            }
          });

          setPetOwnerSearchResults(combinedResults);
          setIsSearchingPetOwners(false);
        })
        .catch((error) => {
          console.error('Error searching Pet Guardians:', error);
          // Fallback to local vets search
          const searchTerm = petOwnerSearch.toLowerCase().trim();
          const matchingVets = filterAllowedPetOwners((vets || []).filter(vet => {
            const vetName = getVetDisplayName(vet).toLowerCase();
            return vetName.includes(searchTerm);
          }).map(vet => ({
            ...vet,
            display_name: getVetDisplayName(vet),
            is_staff: true,
            user_type: USER_TYPES.VETERINARIAN
          })));

          setPetOwnerSearchResults(matchingVets);
          setIsSearchingPetOwners(false);
        });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [petOwnerSearch]);

  // Generate time slots when working hours change
  useEffect(() => {
    const slots = generateTimeSlots(workingHours);
    setAvailableTimeSlots(slots);
  }, [workingHours]);

  // Filter time slots based on selected date and current time
  // - For future dates: show all available time slots
  // - For today: show only time slots that are 15+ minutes in the future
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return availableTimeSlots;
    }

    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    const selectedDayIndex = selectedDateObj.getDay();

    // Check if selected date is today
    const isToday = selectedDateObj.toDateString() === today.toDateString();

    let filteredSlots = availableTimeSlots.filter(slot => slot.dayIndex === selectedDayIndex);

    if (isToday) {
      // Filter out past time slots for today
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      const originalSlotsCount = filteredSlots.length;
      filteredSlots = filteredSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;

        // Show time slots that are at least 15 minutes in the future (reduced from 30 to be less restrictive)
        const isFutureSlot = slotTimeInMinutes >= (currentTimeInMinutes + 15);


        return isFutureSlot;
      });

    }

    return filteredSlots;
  };

  // Initialize Flatpickr for date picker only
  useEffect(() => {
    if (isOpen && dateInputRef.current) {
      // Initialize date picker
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }

      // Create array of available days based on working hours
      const availableDays = workingHours
        .map(wh => dayStringToIndex(wh.day))
        .filter((idx) => idx !== undefined);

      // Enable working hours restriction and disable past dates
      const disableFunction = workingHours.length > 0 ? [
        function (date) {
          // Disable past dates
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) {
            return true;
          }

          // Disable dates that are not in working hours
          const dayOfWeek = date.getDay();
          const isDisabled = !availableDays.includes(dayOfWeek);
          return isDisabled;
        }
      ] : [
        function (date) {
          // Disable past dates when no working hours are set
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ];

      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          setNewOutpatient(prev => ({ ...prev, date: dateStr, time: "" })); // Reset time when date changes
          // Close time dropdown when date changes
          setShowTimeDropdown(false);
        }
      });
    }

    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, [isOpen, workingHours]);

  // Reset the nested add-pet modal whenever this modal closes
  useEffect(() => {
    if (!isOpen) setShowAddPetModal(false);
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewOutpatient({
        id: null,
        pet_owner: "",
        pet: "",
        vet: "",
        reason: "",
        date: "",
        time: "",
        type: "",
      });
      setDownpayment(0);
      // Commented out SelectPaymentModal reset - will be used later
      // setShowSelectPaymentModal(false);
      // setSelectedPaymentMethod(null);
      // setPaymentNotes("");
      setShowPetOwnerDropdown(false);
      setIsSearchingPetOwners(false);
      setVetSearch("");
      setSelectedVet(null);
      setShowVetDropdown(false);
      setShowTimeDropdown(false);

      // Apply prefill data if provided (from new patient wizard)
      if (prefillPetOwner) {
        const displayName = prefillPetOwner.display_name || prefillPetOwner.full_name ||
          `${prefillPetOwner.first_name || ''} ${prefillPetOwner.last_name || ''}`.trim();
        setPetOwnerSearch(displayName);
        setSelectedPetOwner(prefillPetOwner);

        if (prefillPet) {
          setPetOwnerPets([prefillPet]);
          setSelectedPet(prefillPet);
          setIsLoadingPets(false);
        } else {
          setPetOwnerPets([]);
          setSelectedPet(null);
          setIsLoadingPets(false);
        }
        setPetOwnerSearchResults([]);
      } else {
        setPetOwnerSearch("");
        setSelectedPetOwner(null);
        setPetOwnerPets([]);
        setIsLoadingPets(false);
        setPetOwnerSearchResults([]);
        setSelectedPet(null);
      }
    }
  }, [isOpen, prefillPetOwner, prefillPet]);

  // Commented out payment method selection useEffect - will be used later
  // useEffect(() => {
  //   if (!isOpen) return;
  //   if (Number(downpayment) > 0 && !loadingPaymentTypes && (paymentTypes || []).length > 0 && !selectedPaymentMethod) {
  //     const isCash = (t) => {
  //       const a = (t?.label || t?.type_display || t?.value || '').toString().toLowerCase();
  //       return a.includes('cash');
  //     };
  //     const def = (paymentTypes || []).find(isCash) || (paymentTypes || [])[0];
  //     if (def) {
  //       setSelectedPaymentMethod(def.value);
  //     }
  //   }
  //   if (!(Number(downpayment) > 0) && selectedPaymentMethod) {
  //     setSelectedPaymentMethod(null);
  //   }
  // }, [isOpen, downpayment, loadingPaymentTypes, paymentTypes, selectedPaymentMethod]);

  // Auto-select current user as veterinarian if applicable
  useEffect(() => {
    if (!isOpen || selectedVet) return;

    try {
      const user = authUtils.getCurrentUser();
      const userType = user?.user_type || authUtils.getUserType();
      if ((userType === 'VETERINARIAN' || user?.role === 'VETERINARIAN') && Array.isArray(vets) && vets.length > 0) {
        const currentUserId = user?.id || user?.user_id;
        const myVet = vets.find((v) => v.user_id === currentUserId);
        if (myVet) {
          setSelectedVet(myVet);
          setVetSearch(getVetDisplayName(myVet));
        }
      }
    } catch (e) {
      // silently ignore
    }
  }, [isOpen, vets, selectedVet, getVetDisplayName]);

  const handleSave = async (overridePaymentNotes = null) => {
    try {
      // Get clinic ID using multiple approaches
      const clinicId = getClinicId();
      if (!clinicId) {
        showErrorToast(t("receptionOutpatients.admit.toasts.clinicMissing"));
        return;
      }

      // Validate required fields
      if (!selectedPetOwner) {
        showErrorToast(t("receptionOutpatients.admit.toasts.petGuardianRequired"));
        return;
      }
      if (!selectedPet) {
        showErrorToast(t("receptionOutpatients.admit.toasts.petRequired"));
        return;
      }
      if (!selectedVet) {
        showErrorToast(t("receptionOutpatients.admit.toasts.vetRequired"));
        return;
      }

      // Commented out payment method validation - will be used later
      // if (Number(downpayment) > 0 && !selectedPaymentMethod) {
      //   showErrorToast("Please select a payment method");
      //   return;
      // }

      // Format visit_date as datetime string (YYYY-MM-DD HH:MM:SS)

      // Use override notes if provided
      const notesToUse = overridePaymentNotes || "";

      // Prepare API payload with required fields
      const apiPayload = {
        pet_owner_id: selectedPetOwner.id,
        pet_id: selectedPet.id,
        vet_id: selectedVet.user_id,
        clinic_id: clinicId,
        downpayment: downpayment || 0,
        reason: newOutpatient.reason
      };
      // Commented out selectedPaymentMethod logic - will be used later
      // if (Number(downpayment) > 0 && selectedPaymentMethod) {
      //   apiPayload.payment_type_id = selectedPaymentMethod;
      // }
      if (notesToUse && notesToUse.trim()) {
        apiPayload.payment_note = notesToUse.trim();
      }

      // Call the API
      const response = await outpatientAPI.createOutpatientVisit(apiPayload);
      onClose();
      showSuccessToast(t("receptionOutpatients.admit.toasts.saveSuccess"));
      if (response.data) {
        // Prepare display data for the table
        const outpatientData = {
          id: response.data.id || Date.now(),
          pet_owner_id: selectedPetOwner.id,
          pet_id: selectedPet.id,
          vet_id: selectedVet.user_id,
          reason: newOutpatient.reason,
          date: newOutpatient.date,
          time: newOutpatient.time,
          appointment_type_id: newOutpatient.type,
          downpayment: downpayment,
          // Display data for table
          pet_owner: selectedPetOwner
            ? selectedPetOwner.display_name ||
            selectedPetOwner.full_name ||
            `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}` ||
            fallbackLabels.petGuardian
            : fallbackLabels.petGuardian,
          pet: selectedPet ? selectedPet.name : fallbackLabels.pet,
          vet: selectedVet
            ? getVetDisplayName(selectedVet)
            : fallbackLabels.vet,
        };

        await onSave(outpatientData);

      }
    } catch (error) {
      console.error('Error creating outpatient visit:', error);
      showErrorToast(
        error.response?.data?.message || t("receptionOutpatients.admit.toasts.saveFailed")
      );
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="reception-modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)'
      }}>
        <div
          className="reception-modal"
          dir={i18n.dir()}
          lang={i18n.language}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
          <div className="reception-modal-header" style={{
            padding: '0 32px 16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827'
            }}>
              🏥 {t("receptionOutpatients.admit.title")}
            </h2>
            <button
              className="reception-modal-close"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              ✕
            </button>
          </div>
          <form
            className="reception-modal-form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            style={{
              paddingLeft: '32px',
              paddingRight: '32px'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👤 {t("receptionOutpatients.admit.fields.petGuardian.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowPetOwnerDropdown(!showPetOwnerDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedPetOwner ? '#111827' : '#9ca3af' }}>
                      {selectedPetOwner
                        ? selectedPetOwner.display_name ||
                        selectedPetOwner.full_name ||
                        `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}` ||
                        fallbackLabels.petGuardian
                        : t("receptionOutpatients.admit.fields.petGuardian.placeholder")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showPetOwnerDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showPetOwnerDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionOutpatients.admit.fields.petGuardian.searchPlaceholder")}
                          value={petOwnerSearch}
                          onChange={(e) => {
                            setPetOwnerSearch(e.target.value);
                            // Clear selected Pet Guardian when user starts typing to search for a new one
                            if (selectedPetOwner && e.target.value) {
                              setSelectedPetOwner(null);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {isSearchingPetOwners ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280'
                          }}>
                            <div style={{
                              display: 'inline-block',
                              width: '20px',
                              height: '20px',
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #3b82f6',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            <div style={{ marginTop: '8px', fontSize: '14px' }}>
                              {t("receptionOutpatients.admit.search.searching")}
                            </div>
                          </div>
                        ) : petOwnerSearchResults.length > 0 ? (
                          petOwnerSearchResults.map((g) => (
                            <div
                              key={g.id}
                              style={{
                                cursor: "pointer",
                                padding: "12px 16px",
                                borderBottom: '1px solid #f3f4f6',
                                transition: 'background-color 0.2s'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedPetOwner(g);
                                setShowPetOwnerDropdown(false);
                                setPetOwnerSearchResults([]);
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: '500', color: '#111827' }}>
                                  {g.display_name ||
                                    g.full_name ||
                                    `${g.first_name} ${g.last_name}` ||
                                    fallbackLabels.petGuardian}
                                </div>
                                {g.last_completed_appointment_relation && (
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                    {t("receptionAppointments.create.fields.lastVisit.dropdown", {
                                      date: new Date(g.last_completed_appointment_relation.appointment_at).toLocaleDateString(locale),
                                      status: g.last_completed_appointment_relation.status,
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : petOwnerSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionOutpatients.admit.fields.petGuardian.empty")}
                            <div className="" style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                              <button
                                type="button"
                                className="modern-action-btn vet-button-color"
                                onClick={() => {
                                  setShowPetOwnerDropdown(false);
                                  setPetOwnerSearch('');
                                  setPetOwnerSearchResults([]);
                                  setShowAddPetOwnerModal(true);
                                }}
                              >
                                {t("receptionOutpatients.admit.fields.petGuardian.add")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionOutpatients.admit.fields.petGuardian.hint")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  🐾 {t("receptionAppointments.create.fields.pet.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={selectedPet ? selectedPet.name : ""}
                  onChange={(e) => {
                    const selectedPetName = e.target.value;
                    if (selectedPetName === "__add_new__") {
                      setShowAddPetModal(true);
                      return;
                    }
                    const pet = petOwnerPets.find(p => p.name === selectedPetName);
                    setSelectedPet(pet || null);
                  }}
                  disabled={!selectedPetOwner || isLoadingPets}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: (!selectedPetOwner || isLoadingPets) ? '#f9fafb' : 'white',
                    cursor: (!selectedPetOwner || isLoadingPets) ? 'not-allowed' : 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">
                    {!selectedPetOwner
                      ? t("receptionAppointments.create.fields.pet.guardianHint")
                      : isLoadingPets
                        ? t("receptionAppointments.create.fields.pet.loading")
                        : availablePets.length === 0
                          ? t("receptionAppointments.create.fields.pet.empty")
                          : t("receptionAppointments.create.fields.pet.placeholder")
                    }
                  </option>
                  {!isLoadingPets && availablePets.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                  {selectedPetOwner && !isLoadingPets && (
                    <option value="__add_new__">
                      ➕ {t("receptionAppointments.create.fields.pet.addNew") || 'Add New Pet'}
                    </option>
                  )}
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👨‍⚕️ {t("receptionAppointments.create.fields.vet.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowVetDropdown(!showVetDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedVet ? '#111827' : '#9ca3af' }}>
                      {selectedVet
                        ? getVetDisplayName(selectedVet)
                        : t("receptionAppointments.create.fields.vet.placeholder")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showVetDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showVetDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionAppointments.create.fields.vet.searchPlaceholder")}
                          value={vetSearch}
                          onChange={(e) => setVetSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {vets.filter((v) => {
                          const displayName = getVetDisplayName(v);
                          return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                        }).length > 0 ? (
                          vets
                            .filter((v) => {
                              const displayName = getVetDisplayName(v);
                              return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                            })
                            .map((v) => {
                              const displayName = getVetDisplayName(v);
                              return (
                                <div
                                  key={v.user_id}
                                  style={{
                                    cursor: "pointer",
                                    padding: "12px 16px",
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedVet(v);
                                    setVetSearch(displayName);
                                    setShowVetDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {displayName}
                                </div>
                              );
                            })
                        ) : vetSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.empty")}
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.hint")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  📝 {t("receptionOutpatients.admit.fields.reason.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={newOutpatient.reason}
                  onChange={(e) =>
                    setNewOutpatient((a) => ({
                      ...a,
                      reason: e.target.value,
                    }))
                  }
                  placeholder={t("receptionOutpatients.admit.fields.reason.placeholder")}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  💰 {t("receptionOutpatients.admit.fields.downpayment.label")}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="reception-modal-input"
                  value={downpayment}
                  onChange={(e) => setDownpayment(e.target.value === '' ? 0 : Number(e.target.value))}
                  placeholder={t("receptionOutpatients.admit.fields.downpayment.placeholder")}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>

            </div>


            <div className="reception-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end',
              paddingTop: '10px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="reception-action-btn"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                {t("receptionOutpatients.admit.buttons.cancel")}
              </button>
              <button
                type="button"
                className="reception-action-btn"
                onClick={async () => {
                  // Commented out payment modal logic - will be used later
                  // if (Number(downpayment) > 0) {
                  //   setShowSelectPaymentModal(true);
                  //   return;
                  // }

                  // For now, always proceed with admission directly
                  await handleSave();
                }}
                style={{
                  width: '40%',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563eb';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
              >
                💾 {t("receptionOutpatients.admit.buttons.save")}
                {/* Commented out payment modal button text - will be used later */}
                {/* {Number(downpayment) > 0 ? '💳 Select Payment & Admit' : '💾 Admit Outpatient'} */}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Commented out SelectPaymentModal - will be used later */}
      {/* 
      <SelectPaymentModal
        isOpen={showSelectPaymentModal}
        onClose={() => setShowSelectPaymentModal(false)}
        onSave={(data) => {
          setSelectedPaymentMethod(data.method);
          setPaymentNotes(data.notes || "");
          setShowSelectPaymentModal(false);
          // Automatically proceed with admission after payment selection, passing notes directly
          setTimeout(() => handleSave(data.notes || ""), 100);
        }}
        paymentNotes={paymentNotes}
        paymentType={selectedPaymentMethod}
        price={downpayment}
      />
      */}

      <AddPetOwnerModal
        isOpen={showAddPetOwnerModal}
        onClose={() => setShowAddPetOwnerModal(false)}
        onSave={(data) => {
          if (data?.ownerName) {
            setPetOwnerSearch(data.ownerName);
          }
          setShowAddPetOwnerModal(false);
        }}
      />

      <QuickAddPetModal
        isOpen={showAddPetModal}
        onClose={() => setShowAddPetModal(false)}
        onPetAdded={(newPet) => {
          setPetOwnerPets((prev) => [...prev, newPet]);
          setSelectedPet(newPet);
          setShowAddPetModal(false);
        }}
        ownerId={selectedPetOwner?.id}
        ownerName={
          selectedPetOwner
            ? selectedPetOwner.display_name || selectedPetOwner.full_name || `${selectedPetOwner.first_name || ''} ${selectedPetOwner.last_name || ''}`.trim()
            : ''
        }
        userTypeColor="#3b82f6"
      />
    </>
  );
};

export default AdmitOutpatientModal;
